import { formatDistanceToNow } from "date-fns";

export function formatRelative(at: string | number): string {
  const d = new Date(at);
  return formatDistanceToNow(d, { addSuffix: true });
}

export function toDateTimeLocal(epochMs: number): string {
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getFullYear() +
    "-" +
    pad(d.getMonth() + 1) +
    "-" +
    pad(d.getDate()) +
    "T" +
    pad(d.getHours()) +
    ":" +
    pad(d.getMinutes())
  );
}

export function fromDateTimeLocal(value: string): number {
  return new Date(value).getTime();
}

export function formatDateTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDayTime(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRange(from: number, to: number): string {
  const f = new Date(from);
  const t = new Date(to);
  const sameDay =
    f.getFullYear() === t.getFullYear() &&
    f.getMonth() === t.getMonth() &&
    f.getDate() === t.getDate();
  const dt = (d: Date) =>
    d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  const time = (d: Date) =>
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `${dt(f)} - ${time(t)}` : `${dt(f)} - ${dt(t)}`;
}

const HOTEL_TZ = "Europe/Madrid";
const HOTEL_CHECKIN_HOUR = 15;
const HOTEL_CHECKOUT_HOUR = 11;
const HOTEL_DEFAULT_NIGHTS = 3;

function tzOffsetMinutes(utcMs: number, tz: string): number {
  const offsetStr =
    new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "longOffset",
    })
      .formatToParts(new Date(utcMs))
      .find((p) => p.type === "timeZoneName")?.value ?? "GMT+00:00";
  if (offsetStr === "GMT") return 0;
  const m = offsetStr.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
  if (!m) return 60;
  const sign = m[1] === "+" ? 1 : -1;
  return sign * (parseInt(m[2]) * 60 + parseInt(m[3] ?? "0"));
}

function dateOfInTz(utcMs: number, tz: string) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(utcMs));
  return {
    y: parseInt(parts.find((p) => p.type === "year")!.value),
    m: parseInt(parts.find((p) => p.type === "month")!.value) - 1,
    d: parseInt(parts.find((p) => p.type === "day")!.value),
  };
}

function tzDateAt(
  y: number,
  m: number,
  d: number,
  hour: number,
  tz: string
): number {
  const asIfUtc = Date.UTC(y, m, d, hour, 0, 0, 0);
  return asIfUtc - tzOffsetMinutes(asIfUtc, tz) * 60 * 1000;
}

export function defaultHotelCheckInOut(): { from: number; until: number } {
  const now = Date.now();
  const today = dateOfInTz(now, HOTEL_TZ);
  let from = tzDateAt(today.y, today.m, today.d, HOTEL_CHECKIN_HOUR, HOTEL_TZ);
  if (from <= now) {
    from = tzDateAt(today.y, today.m, today.d + 1, HOTEL_CHECKIN_HOUR, HOTEL_TZ);
  }
  const fromDay = dateOfInTz(from, HOTEL_TZ);
  const until = tzDateAt(
    fromDay.y,
    fromDay.m,
    fromDay.d + HOTEL_DEFAULT_NIGHTS,
    HOTEL_CHECKOUT_HOUR,
    HOTEL_TZ
  );
  return { from, until };
}

export function todayIso(): string {
  const today = dateOfInTz(Date.now(), HOTEL_TZ);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${today.y}-${pad(today.m + 1)}-${pad(today.d)}`;
}

export function formatCountdown(ms: number): string {
  if (ms <= 0) return "0m";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function formatCountdownParts(ms: number): {
  d: number;
  h: number;
  m: number;
  s: number;
} {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  return {
    d: Math.floor(totalSec / 86400),
    h: Math.floor((totalSec % 86400) / 3600),
    m: Math.floor((totalSec % 3600) / 60),
    s: totalSec % 60,
  };
}
